import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import ProductCard from "../components/ProductCard";
import { toast } from "react-toastify";
import { CartContext } from "../context/CartContext";
import StarRating from "../components/StarRating";
import ImageLightbox from "../components/ImageLightbox";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import giftDeodorant from "../assets/quà tặng/chai khử mùi.jpg";
import giftSocks from "../assets/quà tặng/vớ.png";
import SizeGuideModal from "../components/SizeGuideModal";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const sizeSectionRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Fetching product with ID:", id);
      const response = await api.get(`/products/${id}`);
      console.log("Product response:", response.data);

      setProduct(response.data);

      if (response.data.colors?.length > 0) {
        const firstColor = response.data.colors[0];
        setSelectedColor(
          typeof firstColor === "string" ? firstColor : firstColor.name,
        );
      }
      // Không tự động chọn size để buộc người dùng phải chọn
      if (response.data.sizes?.length > 0) {
        setSelectedSize(null);
      }

      fetchRelatedProducts(response.data._id);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching product:", error);
      setError(error.response?.data?.error || "Không thể tải sản phẩm");
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (productId) => {
    try {
      if (!productId) {
        console.warn("No product ID provided for fetching related products");
        return;
      }
      const response = await api.get(`/products/${productId}/related`);
      setRelatedProducts(response.data);
    } catch (error) {
      console.error("Error fetching related products:", error);
      setRelatedProducts([]);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN").format(price);
  };

  const handleAddToCart = (productToAdd = null) => {
    if (productToAdd) {
      const payload = {
        productId: productToAdd._id,
        qty: 1,
        selectedColor: productToAdd.colors?.[0] || "Mặc định",
        selectedSize: productToAdd.sizes?.[0] || "One Size",
        price: productToAdd.price,
        name: productToAdd.name,
        image: productToAdd.image || productToAdd.images?.[0],
      };

      console.log("🛒 Payload to add cart (related product):", payload);
      addToCart(payload);
      toast.success("✅ Đã thêm vào giỏ hàng!");
      return;
    }

    if (!product) return;

    // Kiểm tra nếu sản phẩm có size mà người dùng chưa chọn
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.warn("⚠️ Vui lòng chọn kích thước sản phẩm!");
      sizeSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const payload = {
      productId: product._id,
      qty: quantity,
      selectedColor: selectedColor || "Mặc định",
      selectedSize: selectedSize || "One Size",
      price: product.price,
      name: product.name,
      image: product.image || product.images?.[0],
    };

    console.log("🛒 Payload to add cart (main product):", payload);
    addToCart(payload);
    toast.success("✅ Đã thêm vào giỏ hàng!");
  };

  const handleBuyNow = async () => {
    if (!product) return;

    // Kiểm tra nếu sản phẩm có size mà người dùng chưa chọn
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.warn("⚠️ Vui lòng chọn kích thước sản phẩm!");
      sizeSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const payload = {
      productId: product._id,
      qty: quantity,
      selectedColor: selectedColor || "Mặc định",
      selectedSize: selectedSize || "One Size",
      price: product.price,
      name: product.name,
      image: product.image || product.images?.[0],
    };

    console.log("🚀 Buying Now:", payload);
    await addToCart(payload);
    navigate("/checkout");
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
    if (!isFavorite) {
      toast.success("❤️ Đã thêm vào danh sách yêu thích!");
    } else {
      toast.info("💔 Đã xóa khỏi danh sách yêu thích");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 transition-colors duration-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">Đang tải sản phẩm...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 transition-colors duration-500">
        <div className="text-center">
          <svg
            className="w-24 h-24 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Không tìm thấy sản phẩm
          </h2>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            {error || "Sản phẩm này có thể đã bị xóa hoặc không tồn tại"}
          </p>
          <button
            onClick={() => navigate("/products")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← Quay lại trang sản phẩm
          </button>
        </div>
      </div>
    );
  }

  const images =
    product.images && product.images.length > 0
      ? product.images
      : [product.image];
  const defaultImage = "https://placehold.co/400x400?text=No+Image";

  return (
    <>

      <div 
        className="min-h-screen bg-gray-50 dark:bg-slate-950 pt-20 md:pt-36 pb-32 sm:pb-8 overflow-x-hidden transition-colors duration-500"
        style={{ 
          zoom: "0.8",
          MozTransform: "scale(0.8)",
          MozTransformOrigin: "top center"
        }}
      >
        <Navbar />
        <SEO
          title={product.name}
          description={`Mua ngay ${product.name} chính hãng tại TaHa Shoes. ${product.description?.substring(0, 100)}... Cam kết chất lượng, bảo hành 12 tháng, đổi trả 7 ngày.`}
          image={product.image || images[0]}
          type="product"
          keywords={`${product.name}, ${product.brand}, giày ${product.category}, sneaker chính hãng, taha shoes`}
          schema={[
            {
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "image": product.image || images[0],
              "description": product.description?.substring(0, 200),
              "offers": {
                "@type": "Offer",
                "url": window.location.href,
                "priceCurrency": "VND",
                "price": product.price,
                "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "itemCondition": "https://schema.org/NewCondition"
              },
              "brand": {
                "@type": "Brand",
                "name": product.brand || "Tahashoes"
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Trang chủ",
                  "item": "https://tahashoes.vn/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": product.category || "Sản phẩm",
                  "item": `https://tahashoes.vn/products?category=${product.category}`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": product.name,
                  "item": window.location.href
                }
              ]
            }
          ]}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center flex-wrap gap-y-1 gap-x-1.5 text-[10px] sm:text-sm text-gray-500 mb-3 sm:mb-4 overflow-hidden">
            <Link
              to="/"
              className="hover:text-blue-600 whitespace-nowrap flex-shrink-0"
            >
              Trang chủ
            </Link>
            <span className="opacity-50 flex-shrink-0">/</span>
            <Link
              to="/products"
              className="hover:text-blue-600 whitespace-nowrap flex-shrink-0"
            >
              Sản phẩm
            </Link>
            <span className="opacity-50 flex-shrink-0">/</span>
            <span className="text-gray-900 dark:text-white transition-colors truncate min-w-0">
              {product.name}
            </span>
          </nav>

          <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-sm sm:shadow-lg border border-gray-100 dark:border-slate-800 overflow-hidden mb-4 sm:mb-8 transition-colors duration-500">
            <div className="grid md:grid-cols-2 gap-4 lg:gap-6 p-3 sm:p-5 lg:p-6">
              <div className="space-y-4">
                <div
                  className="w-full aspect-square bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-gray-100 dark:border-slate-800 sm:border-2 sm:border-gray-200 dark:sm:border-slate-700 relative cursor-zoom-in group/main flex items-center justify-center"
                  onClick={() => setIsLightboxOpen(true)}
                >
                  <img
                    src={images[selectedImage] || defaultImage}
                    alt={`${product.name} - Giày sneaker chính hãng tại Tahashoes`}
                    className="w-full h-full object-contain p-2 sm:p-6 transition-transform duration-500 group-hover/main:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = defaultImage;
                    }}
                  />
                  <div className="absolute inset-0 bg-black/5 opacity-0 group-hover/main:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white/90 p-2 rounded-full shadow-lg transform translate-y-4 group-hover/main:translate-y-0 transition-transform">
                      <svg
                        className="w-6 h-6 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                        />
                      </svg>
                    </div>
                  </div>
                  {product.discount > 0 && (
                    <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-red-500 text-white px-2 py-0.5 sm:px-4 sm:py-2 rounded-lg text-[10px] sm:text-sm font-bold shadow-md">
                      -{product.discount}%
                    </div>
                  )}
                </div>

                {images.length > 1 && (
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center sm:justify-start">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 w-11 sm:w-16 ${
                          selectedImage === index
                            ? "border-blue-600 shadow-sm"
                            : "border-gray-100 dark:border-slate-800 sm:border-gray-200 dark:sm:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500 bg-white dark:bg-slate-900"
                        }`}
                      >
                        <img
                          src={img || defaultImage}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-contain p-1"
                          onError={(e) => {
                            e.target.src = defaultImage;
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Desktop Only: Size under Thumbnails */}
                <div className="hidden md:block mt-6 border-t border-gray-100 pt-4">
                  {product.sizes && product.sizes.length > 0 && (
                    <div ref={sizeSectionRef} className="scroll-mt-20">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                          Kích thước:{" "}
                          <span className="font-normal text-blue-600 dark:text-blue-400">
                            {selectedSize}
                          </span>
                        </label>
                        <button
                          onClick={() => setIsSizeGuideOpen(true)}
                          className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 transition-all"
                        >
                          <span className="text-sm">📏</span> Hướng dẫn chọn
                          size
                        </button>
                      </div>
                      <div className="grid grid-cols-4 lg:grid-cols-5 gap-2">
                        {product.sizes.map((size) => (
                          <button
                            key={size}
                            onClick={() => setSelectedSize(size)}
                            className={`py-2 rounded-xl border-2 transition-all font-bold text-sm flex items-center justify-center ${
                              selectedSize === size
                                ? "border-blue-600 bg-blue-50 dark:bg-blue-600 text-blue-600 dark:text-white shadow-[0_0_15px_rgba(37,99,235,0.1)] scale-105"
                                : "border-gray-100 dark:border-slate-800 hover:border-blue-400 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-900"
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-5">
                {/* Compact Header for Brand and Name */}
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] sm:text-sm text-gray-500 dark:text-slate-400 uppercase tracking-wide font-bold">
                      {product.brand || product.category || "TAHA SHOES"}
                    </p>
                    <span className="text-gray-300">|</span>
                    {product.stock > 0 ? (
                      <span className="text-[10px] sm:text-xs text-green-600 font-bold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                        Còn hàng
                      </span>
                    ) : (
                      <span className="text-[10px] sm:text-xs text-red-600 font-bold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                        Hết hàng
                      </span>
                    )}
                  </div>

                  <div>
                    <h1 className="text-sm sm:text-2xl font-bold text-gray-900 dark:text-white mb-0.5 leading-snug">
                      {product.name}
                    </h1>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <StarRating
                        rating={product.rating || 5}
                        size="w-3.5 h-3.5 sm:w-5 sm:h-5"
                      />
                      <span className="text-[10px] sm:text-sm text-gray-600 dark:text-slate-400">
                        {product.rating || 5.0} ({product.reviewCount || 0} đánh
                        giá)
                      </span>
                      {product.soldCount > 0 && (
                        <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4 pl-2 sm:pl-4 border-l border-gray-300 dark:border-slate-700">
                          <span className="text-[10px] sm:text-sm text-gray-500 dark:text-slate-400 font-medium whitespace-nowrap">
                            Đã bán:
                          </span>
                          <span className="text-xs sm:text-base font-bold text-blue-600 dark:text-blue-400">
                            {product.soldCount}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-b dark:border-slate-800 py-2 sm:py-4">
                  <div className="flex items-baseline flex-wrap gap-2 sm:gap-4">
                    <span className="text-lg sm:text-3xl font-bold text-red-600">
                      {formatPrice(product.price)}₫
                    </span>
                    {product.originalPrice &&
                      product.originalPrice > product.price && (
                        <span className="text-xs sm:text-xl text-gray-400 line-through">
                          {formatPrice(product.originalPrice)}₫
                        </span>
                      )}
                    {product.discount > 0 && product.originalPrice && (
                      <span className="bg-red-100 text-red-600 px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[9px] sm:text-[12px] font-semibold">
                        Tiết kiệm{" "}
                        {formatPrice(product.originalPrice - product.price)}₫
                      </span>
                    )}
                  </div>
                </div>

                {/* Gift Section */}
                {product?.gifts && product.gifts.length > 0 && (
                  <div className="mt-2 sm:mt-3 p-2 sm:p-2.5 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40 border border-orange-100 dark:border-orange-900/30 rounded-xl sm:rounded-2xl shadow-sm border-l-4 border-l-orange-500 max-w-[320px]">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                      <div className="bg-orange-100 dark:bg-orange-800 p-1 sm:p-1 rounded-lg text-sm sm:text-base flex items-center justify-center">
                        🎁
                      </div>
                      <div>
                        <h4 className="font-bold text-orange-700 dark:text-orange-400 text-[10px] sm:text-[13px] uppercase tracking-wider">
                          Quà tặng đặc biệt
                        </h4>
                      </div>
                    </div>

                    <div className="relative flex items-center gap-2 sm:gap-3">
                      <div className="flex-1 bg-white dark:bg-slate-800/40 p-1 sm:p-1.5 rounded-lg sm:rounded-xl border border-orange-100 dark:border-orange-900/30 flex flex-col items-center group transition-all">
                        <div className="w-8 h-8 sm:w-14 sm:h-14 mb-1 sm:mb-1 overflow-hidden rounded-lg bg-gray-50 dark:bg-slate-800">
                          <img
                            src={giftDeodorant}
                            alt="Chai khử mùi giày"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        </div>
                        <span className="text-[8px] sm:text-[11px] font-bold text-gray-800 dark:text-gray-200 text-center leading-tight">
                          Chai khử mùi
                        </span>
                      </div>

                      <div className="flex-shrink-0 text-orange-400 font-bold text-xs sm:text-lg z-10">
                        +
                      </div>

                      <div className="flex-1 bg-white dark:bg-slate-800/40 p-1 sm:p-1.5 rounded-lg sm:rounded-xl border border-orange-100 dark:border-orange-900/30 flex flex-col items-center group transition-all">
                        <div className="w-8 h-8 sm:w-14 sm:h-14 mb-1 sm:mb-1 overflow-hidden rounded-lg bg-gray-50 dark:bg-slate-800">
                          <img
                            src={giftSocks}
                            alt="Đôi vớ"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        </div>
                        <span className="text-[8px] sm:text-[11px] font-bold text-gray-800 dark:text-gray-200 text-center leading-tight">
                          Đôi vớ
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  {(product?.shortDescription || product?.description) && (
                    <div className="space-y-1 sm:space-y-2">
                      {(product.shortDescription || product.description)
                        .split(/\r?\n/)
                        .filter((line) => line.trim() !== "").length > 1 ||
                      (
                        product.shortDescription || product.description
                      ).includes("•") ||
                      (
                        product.shortDescription || product.description
                      ).includes("- ") ? (
                        <div className="bg-gray-50/50 dark:bg-slate-900/30 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-gray-100 dark:border-slate-800 shadow-sm">
                          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1 sm:gap-y-2">
                            {(product.shortDescription || product.description)
                              .split(/\r?\n/)
                              .filter((line) => line.trim() !== "")
                              .map((line, idx) => {
                                const cleanLine = line
                                  .replace(/^[•\-*]\s*/, "")
                                  .trim();
                                if (!cleanLine) return null;
                                return (
                                  <li
                                    key={idx}
                                    className="flex items-start gap-3 text-gray-700 dark:text-slate-300 leading-relaxed"
                                  >
                                    <span className="mt-1 bg-green-500 rounded-full p-0 sm:p-1 flex-shrink-0 shadow-sm">
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={4}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    </span>
                                    <span className="font-medium text-[12px] sm:text-[15px]">
                                      {cleanLine}
                                    </span>
                                  </li>
                                );
                              })}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-gray-600 leading-relaxed text-[15px]">
                          {product.shortDescription || product.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 pt-3 sm:pt-4">
                  {product.colors && product.colors.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2 text-left">
                        Màu sắc:{" "}
                        <span className="font-normal text-blue-600 dark:text-blue-400">
                          {selectedColor}
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-2 -mx-1 px-1">
                        {product.colors.map((colorObj, idx) => {
                          const colorName =
                            typeof colorObj === "string"
                              ? colorObj
                              : colorObj.name;
                          const colorImage =
                            typeof colorObj === "string"
                              ? null
                              : colorObj.image;

                          return (
                            <button
                              key={idx}
                              onClick={() => {
                                setSelectedColor(colorName);
                                if (colorImage) {
                                  const imgIndex = images.indexOf(colorImage);
                                  if (imgIndex !== -1) {
                                    setSelectedImage(imgIndex);
                                  }
                                }
                              }}
                                className={`group flex items-center gap-1 sm:gap-1.5 p-0.5 sm:p-1.5 rounded-lg border-2 transition-all flex-shrink-0 ${
                                  selectedColor === colorName
                                    ? "border-blue-600 bg-blue-50 dark:bg-blue-600 shadow-md ring-1 ring-blue-600/20 transition-all duration-300"
                                    : "border-gray-100 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900"
                                }`}
                            >
                              {colorImage && (
                                <div className="w-5 h-5 sm:w-8 sm:h-8 rounded-md overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
                                  <img
                                    src={colorImage}
                                    alt={colorName}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                  />
                                </div>
                              )}
                              <span
                                className={`px-0.5 sm:px-1.5 text-[9px] sm:text-xs font-bold transition-all duration-300 ${
                                  selectedColor === colorName
                                    ? "text-blue-700 dark:text-white"
                                    : "text-gray-700 dark:text-slate-300"
                                }`}
                              >
                                {colorName}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Mobile Only: Size Selection (Hidden on Desktop) */}
                  <div className="md:hidden">
                    {product.sizes && product.sizes.length > 0 && (
                      <div ref={sizeSectionRef} className="scroll-mt-20">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[11px] font-semibold text-gray-900 dark:text-white">
                            Kích thước:{" "}
                            <span className="font-normal text-blue-600">
                              {selectedSize}
                            </span>
                          </label>
                          <button
                            onClick={() => setIsSizeGuideOpen(true)}
                            className="text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 transition-all"
                          >
                            <span className="text-sm">📏</span> Hướng dẫn chọn
                            size
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {product.sizes.map((size) => (
                            <button
                              key={size}
                              onClick={() => setSelectedSize(size)}
                              className={`w-9 h-8 rounded-lg border-2 transition-all font-bold text-[11px] flex items-center justify-center ${
                                selectedSize === size
                                  ? "border-blue-600 bg-blue-50 dark:bg-blue-600 text-blue-600 dark:text-white shadow-[0_0_15px_rgba(37,99,235,0.1)] scale-105"
                                  : "border-gray-100 dark:border-slate-800 hover:border-blue-400 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-900"
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden sm:block">
                  <label className="block text-[11px] sm:text-sm font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-3">
                    Số lượng:
                  </label>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="flex items-center border border-gray-300 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="px-2 py-1 sm:px-4 sm:py-2 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-gray-600 dark:text-slate-400"
                        disabled={quantity <= 1}
                      >
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          setQuantity(
                            Math.max(1, Math.min(product.stock, val)),
                          );
                        }}
                        className="w-10 sm:w-16 text-center border-l border-r border-gray-300 dark:border-slate-700 py-1 sm:py-2 focus:outline-none font-semibold text-sm sm:text-base bg-transparent dark:text-white"
                        min="1"
                        max={product.stock}
                      />
                      <button
                        onClick={() =>
                          setQuantity(Math.min(product.stock, quantity + 1))
                        }
                        className="px-2 py-1 sm:px-4 sm:py-2 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-gray-600 dark:text-slate-400"
                        disabled={
                          quantity >= product.stock || product.stock <= 0
                        }
                      >
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </div>
                    <span className="text-[11px] sm:text-sm text-gray-500 dark:text-slate-400 transition-colors">
                      {product.stock} sản phẩm có sẵn
                    </span>
                  </div>
                </div>

                <div className="hidden sm:flex items-stretch gap-2 sm:gap-4 pt-1 sm:pt-6">
                  <button
                    onClick={() => handleAddToCart()}
                    disabled={product.stock <= 0}
                    className="flex-[2.5] bg-blue-600 text-white py-2 sm:py-3 px-2 sm:px-5 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center gap-2 sm:gap-2 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed shadow-md sm:shadow-[0_8px_16px_rgba(37,99,235,0.15)] disabled:shadow-none min-h-[44px] sm:min-h-[48px]"
                  >
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 ${product.stock <= 0 ? "hidden" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <span className="leading-tight">
                      {product.stock <= 0 ? "Hết hàng" : "Thêm giỏ hàng"}
                    </span>
                  </button>

                  <button
                    onClick={handleBuyNow}
                    disabled={product.stock <= 0}
                    className="flex-[1.5] bg-red-600 text-white py-2 sm:py-3 px-2 sm:px-6 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base hover:bg-red-700 active:scale-95 transition-all flex items-center justify-center gap-1 sm:gap-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed shadow-md sm:shadow-[0_8px_16px_rgba(220,38,38,0.15)] disabled:shadow-none min-h-[44px] sm:min-h-[48px]"
                  >
                    <span
                      className={`text-lg sm:text-xl ${product.stock <= 0 ? "hidden" : ""}`}
                    >
                      ⚡
                    </span>
                    <span className="whitespace-nowrap">
                      {product.stock <= 0 ? "Liên hệ" : "Mua Ngay"}
                    </span>
                  </button>

                  <button
                    onClick={handleToggleFavorite}
                    className={`w-11 h-11 sm:w-12 sm:h-12 border-2 rounded-xl sm:rounded-2xl font-semibold active:scale-90 transition-all flex items-center justify-center flex-shrink-0 group ${
                      isFavorite
                        ? "border-red-100 bg-red-50 text-red-500"
                        : "border-gray-200 dark:border-slate-800 text-gray-400 dark:text-slate-500 hover:bg-red-50 hover:text-red-500 hover:border-red-200"
                    }`}
                  >
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 ${isFavorite ? "fill-current" : "group-hover:fill-current"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-1 sm:gap-4 pt-2 sm:pt-4 border-t dark:border-slate-800">
                  <div className="text-center">
                    <svg
                      className="w-5 h-5 sm:w-7 sm:h-7 text-green-500 mx-auto mb-0.5 sm:mb-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="font-semibold text-[8px] sm:text-sm whitespace-nowrap text-gray-800 dark:text-slate-200 transition-colors">
                      Miễn phí ship
                    </p>
                    <p className="hidden sm:block text-[9px] text-gray-400 sm:text-gray-500">
                      Tất cả đơn
                    </p>
                  </div>
                  <div className="text-center">
                    <svg
                      className="w-5 h-5 sm:w-7 sm:h-7 text-blue-500 mx-auto mb-0.5 sm:mb-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <p className="font-semibold text-[8px] sm:text-sm whitespace-nowrap text-gray-800 dark:text-slate-200 transition-colors">
                      Đổi trả dễ dàng
                    </p>
                    <p className="hidden sm:block text-[9px] text-gray-400 sm:text-gray-500">
                      7 ngày
                    </p>
                  </div>
                  <div className="text-center">
                    <svg
                      className="w-5 h-5 sm:w-7 sm:h-7 text-orange-500 mx-auto mb-0.5 sm:mb-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="font-semibold text-[8px] sm:text-sm whitespace-nowrap text-gray-800 dark:text-slate-200 transition-colors">
                      Bảo mật
                    </p>
                    <p className="hidden sm:block text-[9px] text-gray-400 sm:text-gray-500">
                      100% an toàn
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg overflow-hidden mb-8 border border-gray-100 dark:border-slate-800">
            <div className="border-b border-gray-200 dark:border-slate-800">
              <nav className="flex overflow-x-auto custom-scrollbar-hide">
                <button
                  onClick={() => setActiveTab("description")}
                  className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
                    activeTab === "description"
                      ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
                      : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Mô tả sản phẩm
                </button>
                <button
                  onClick={() => setActiveTab("specs")}
                  className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
                    activeTab === "specs"
                      ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
                      : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Thông số
                </button>
              </nav>
            </div>

            <div className="p-4 sm:p-6">
              {activeTab === "description" && (
                <>
                  <div className="space-y-4 sm:space-y-5">
                    {product.description
                      ? product.description
                          .split(/\r?\n/)
                          .filter((line) => line.trim() !== "")
                          .map((line, idx) => {
                            const isBullet =
                              line.trim().startsWith("-") ||
                              line.trim().startsWith("•");
                            let cleanLine = line
                              .replace(/^[•\-]\s*/, "")
                              .trim();

                            // Detect header pattern like "Title: Content"
                            const colonIndex = cleanLine.indexOf(":");
                            let header = "";
                            let content = cleanLine;

                            if (colonIndex > 0 && colonIndex < 40) {
                              header = cleanLine.substring(0, colonIndex + 1);
                              content = cleanLine
                                .substring(colonIndex + 1)
                                .trim();
                            }

                            if (isBullet) {
                              return (
                                <div key={idx} className="flex gap-4 group">
                                  <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 transition-colors">
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </div>
                                  <p className="text-gray-700 dark:text-slate-300 leading-relaxed text-[13px] sm:text-[15.5px]">
                                    {header && (
                                      <span className="font-bold text-gray-900 dark:text-white border-b-2 border-blue-100 dark:border-blue-900/30 mr-2">
                                        {header}
                                      </span>
                                    )}
                                    <span className="font-medium italic">
                                      {content}
                                    </span>
                                  </p>
                                </div>
                              );
                            }
                            return (
                              <p
                                key={idx}
                                className="text-gray-900 dark:text-white leading-relaxed text-[13px] sm:text-[16px] font-bold border-l-4 border-blue-500 pl-4 py-1 bg-blue-50/30 dark:bg-blue-900/10 rounded-r-lg"
                              >
                                {line}
                              </p>
                            );
                          })
                      : "Chưa có mô tả cho sản phẩm này."}
                  </div>

                  {product.features && product.features.length > 0 && (
                    <div className="mt-8 pt-8 border-t border-gray-100 dark:border-slate-800">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 font-inter">
                        Đặc điểm nổi bật
                      </h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                        {product.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <div className="mt-1 bg-green-100 dark:bg-green-900/20 rounded-full p-1 text-green-600 dark:text-green-400">
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <span className="text-gray-700 dark:text-slate-300 font-medium italic">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {activeTab === "specs" && (
                <div>
                  <h3 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
                    Thông số kỹ thuật
                  </h3>
                  <table className="w-full border-collapse">
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                      <tr>
                        <td className="py-2.5 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white w-1/3 text-[11px] sm:text-sm">
                          Thương hiệu
                        </td>
                        <td className="py-2.5 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm">
                          {product.brand || "TAHA SHOES"}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white text-[11px] sm:text-sm">
                          Danh mục
                        </td>
                        <td className="py-2 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm">
                          {product.category}
                        </td>
                      </tr>
                      {product.subcategory && (
                        <tr>
                          <td className="py-2 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white text-[11px] sm:text-sm">
                            Dòng sản phẩm
                          </td>
                          <td className="py-2 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm italic">
                            {product.subcategory}
                          </td>
                        </tr>
                      )}
                      {product.colors && product.colors.length > 0 && (
                        <tr>
                          <td className="py-2 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white text-[11px] sm:text-sm">
                            Màu sắc
                          </td>
                          <td className="py-2 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm">
                            {product.colors
                              .map((c) => (typeof c === "string" ? c : c.name))
                              .join(", ")}
                          </td>
                        </tr>
                      )}

                      {/* Hiển thị các thông số động từ Admin */}
                      {product.specifications &&
                        Object.entries(product.specifications).map(
                          ([key, value], idx) =>
                            key && (
                              <tr key={idx}>
                                <td className="py-2 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white text-[11px] sm:text-sm">
                                  {key}
                                </td>
                                <td className="py-2 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm">
                                  {value}
                                </td>
                              </tr>
                            ),
                        )}

                      {product.sizes && product.sizes.length > 0 && (
                        <tr>
                          <td className="py-2 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white text-[11px] sm:text-sm">
                            Kích thước
                          </td>
                          <td className="py-2 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm font-medium">
                            {product.sizes.join(", ")}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="py-2 px-3 sm:py-4 sm:px-6 bg-gray-50 dark:bg-slate-900/50 font-bold text-gray-900 dark:text-white text-[11px] sm:text-sm">
                          Tình trạng
                        </td>
                        <td className="py-2 px-3 sm:py-4 sm:px-6 text-gray-700 dark:text-slate-300 text-[11px] sm:text-sm">
                          {product.stock > 0 ? (
                            <span className="text-green-600 dark:text-green-400 font-semibold italic">
                              Còn {product.stock} sản phẩm (Sẵn lưu kho)
                            </span>
                          ) : (
                            <span className="text-red-600 dark:text-red-500 font-semibold italic">
                              Hết hàng
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {relatedProducts.length > 0 && (
            <div className="mt-6 sm:mt-8">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-1">
                Sản phẩm liên quan
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-6">
                {relatedProducts.map((relatedProduct) => (
                  <ProductCard
                    key={relatedProduct._id}
                    product={relatedProduct}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        {isLightboxOpen && (
          <ImageLightbox
            images={images}
            currentIndex={selectedImage}
            onIndexChange={setSelectedImage}
            onClose={() => setIsLightboxOpen(false)}
          />
        )}

        <SizeGuideModal
          isOpen={isSizeGuideOpen}
          onClose={() => setIsSizeGuideOpen(false)}
        />

        {/* Sticky Bottom Action Bar for Mobile */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 p-2 pb-safe-bottom z-[80] shadow-[0_-8px_30px_rgb(0,0,0,0.08)] flex items-stretch gap-2">
          <button
            onClick={handleToggleFavorite}
            className={`w-12 border-2 rounded-xl active:scale-90 transition-all flex items-center justify-center flex-shrink-0 ${
              isFavorite
                ? "border-red-100 bg-red-50 text-red-500"
                : "border-gray-200 dark:border-slate-800 text-gray-400 dark:text-slate-500"
            }`}
          >
            <svg
              className={`w-6 h-6 ${isFavorite ? "fill-current" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>

          <button
            onClick={() => handleAddToCart()}
            disabled={product.stock <= 0}
            className="flex-1 bg-blue-600 text-white rounded-xl font-bold text-xs active:scale-95 transition-all flex flex-col items-center justify-center gap-0.5 disabled:opacity-50 min-h-[50px] shadow-lg shadow-blue-500/20"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <span>{product.stock <= 0 ? "Hết hàng" : "Thêm giỏ"}</span>
          </button>

          <button
            onClick={handleBuyNow}
            disabled={product.stock <= 0}
            className="flex-1 bg-red-600 text-white rounded-xl font-bold text-sm active:scale-95 transition-all flex items-center justify-center gap-1 disabled:opacity-50 min-h-[50px] shadow-lg shadow-red-500/20"
          >
            <span className="text-lg">⚡</span>
            <span>{product.stock <= 0 ? "Liên hệ" : "Mua Ngay"}</span>
          </button>
        </div>
        <div className="md:hidden h-24"></div>
      </div>
    </>
  );
};

export default ProductDetailPage;
